import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { GetHomesByBuilderRequest, GetHomesByBuilderResponse, GetMasterFilesByHomeRequest, GetMasterFilesByHomeResponse, GetOptionsByHomeRequest, GetOptionsByHomeResponse, SituWarpApiClient, GetThemesByBuilderRequest, GetThemesByBuilderResponse } from 'situ-warpapi-client';
import { SituAnalyticsWebApiClient } from 'situanalytics-webapi-client';
import { GetRenderReportRequest, GetRenderReportResponse, SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';
import { GetHomeConfigRequest, GetHomeConfigResponse, GetHomeConfigSlimIdRequest, GetHomeConfigSlimIdResponse, GetMostPopularRenderedHomeByBuilderRequest, GetMostPopularRenderedHomeByBuilderResponse, Home, ListHomesByCompanyRequest, ListHomesByCompanyResponse, ListHomesRequest, ListHomesResponse, ListIfcFilesByHomeIdRequest, ListIfcFilesByHomeIdResponse, ListRenderedOutputsRequest, ListRenderedOutputsResponse, ProductName, RenderedOutput, SituViewWebApiClient } from 'situview-webapi-client';
import { Cache } from './cache.service';
import { ICache } from './theme.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HomeService {
  cacheKey: Guid = Guid.create();
  oldCacheKey: Guid = Guid.create();

  constructor(
    private situViewClient: SituViewWebApiClient,
    private renderSerivceClient: SituRenderServiceWebApiClient,
    private analyticsServiceClient: SituAnalyticsWebApiClient,
    private warpApiServiceClient: SituWarpApiClient
  ) {
    this.oldCacheKey = this.cacheKey;
  }

  @Cache({
    duration: 300000
  })
  ListHomes(request: ListHomesRequest): Promise<ListHomesResponse> {
    return this.situViewClient.listHomes(request);
  }
  
  @Cache({
    duration: 300000
  })
  GetRenderChartData(request: GetRenderReportRequest): Promise<GetRenderReportResponse> {
    return this.renderSerivceClient.getRenderReport(request);
  }

  @Cache({
    duration: 300000
  })
  GetMostPopularHomesByBuilder(request: GetMostPopularRenderedHomeByBuilderRequest): Promise<GetMostPopularRenderedHomeByBuilderResponse> {
    return this.analyticsServiceClient.getMostPopularRenderedHomesByBuilder(request);
  }

  @Cache({
    duration: 900000
  })
  GetHomesByBuilder(request: GetHomesByBuilderRequest): Promise<GetHomesByBuilderResponse> {
    return this.warpApiServiceClient.getHomesByBuilder(request);
  }

  @Cache({
    duration: 600000
  })
  GetMasterFilesByHome(request: GetMasterFilesByHomeRequest): Promise<GetMasterFilesByHomeResponse> {
    return this.warpApiServiceClient.getMasterFilesByHome(request);
  }

  @Cache({
    duration: 600000
  })
  GetOptionsByHome(request: GetOptionsByHomeRequest): Promise<GetOptionsByHomeResponse> {
    return this.warpApiServiceClient.getOptionsByHome(request);
  }
  
  @Cache({
    duration: 600000
  })
  GetThemesByBuilder(request: GetThemesByBuilderRequest): Promise<GetThemesByBuilderResponse> {
    return this.warpApiServiceClient.getThemesByBuilder(request);
  }

  private fiveMinuteCacheDuration: number = 2 * 60 * 1000;
  private homesCache: ICache<Home> | undefined;
  private loadingHomesPromise: Promise<Home[]> | null = null;
  ListHomesByCompany(request: ListHomesByCompanyRequest): Promise<Home[]> {
    request.pageSize = 1000;
    request.isHidden = false;

    if (this.homesCache &&
      request.companyId === this.homesCache.companyId &&
      (Date.now() - this.homesCache.timestamp) < this.fiveMinuteCacheDuration) {
      return Promise.resolve(this.homesCache.data);
    } else if (this.loadingHomesPromise && request.companyId === this.homesCache?.companyId) {
      return this.loadingHomesPromise;
    } else {
      this.loadingHomesPromise = this.situViewClient.listHomesByCompany(request)
        .then((response: ListHomesByCompanyResponse) => {
          if (response.success) {
            this.homesCache = {
              timestamp: Date.now(),
              companyId: request.companyId,
              data: response.homes ?? []
            };
            return this.homesCache.data;
          } else {
            return [];
          }
        })
        .catch((error: any) => {
          console.error(error);
          throw error;
        })
        .finally(() => {
          this.loadingHomesPromise = null;
        });
      return this.loadingHomesPromise;
    }
  }

  private renderedOutputsCache: Map<string, ICache<RenderedOutput>> = new Map();
  private twoMinuteCacheDuration: number = 2 * 60 * 1000;
  private loadingRenderedOutputsPromises: Map<string, Promise<RenderedOutput[]>> = new Map();
  listRenderedOutputs(request: ListRenderedOutputsRequest): Promise<RenderedOutput[]> {
    const cacheKey = `${request.companyId || ''}_${request.productName === undefined ? '' : request.productName}_${request.createdBy || ''}`;
    const cache = this.renderedOutputsCache.get(cacheKey);
  
    if (cache) {
      if ((Date.now() - cache.timestamp) < this.twoMinuteCacheDuration) {
        return Promise.resolve(cache.data);
      }
    }
  
    const loadingPromise = this.loadingRenderedOutputsPromises.get(cacheKey);
    if (loadingPromise) {
      return loadingPromise;
    }
  
    const fetchPromise = this.situViewClient.listRenderedOutputs(request)
      .then((response: ListRenderedOutputsResponse) => {
        if (response.success) {
          const data = response.renderedOutputs as RenderedOutput[];
          this.renderedOutputsCache.set(cacheKey, {
            timestamp: Date.now(),
            data: data,
          });
          return data;
        } else {
          return [];
        }
      })
      .catch((error: any) => {
        throw error;
      })
      .finally(() => {
        this.loadingRenderedOutputsPromises.delete(cacheKey);
      });
  
    this.loadingRenderedOutputsPromises.set(cacheKey, fetchPromise);
    return fetchPromise;
  }
  
  clearRenderedOutputsCache(companyId: string | undefined, productName: ProductName | undefined, userId?: string): void {
    const cacheKey = `${companyId || ''}_${productName === undefined ? '' : productName}_${userId || ''}`;
    if (this.renderedOutputsCache.delete(cacheKey)) {
      this.cacheInvalidationSubject.next();
    }
  }

  private cacheInvalidationSubject = new Subject<void>();
  cacheInvalidation$ = this.cacheInvalidationSubject.asObservable();
  
  @Cache({
    duration: 60000
  })
  ListIfcFilesByHomeId(request: ListIfcFilesByHomeIdRequest): Promise<ListIfcFilesByHomeIdResponse> {
    return this.situViewClient.listIfcFilesByHomeId(request);
  }
  
  @Cache({
    duration: 1800000
  })
  GetHomeConfigId(request: GetHomeConfigSlimIdRequest): Promise<GetHomeConfigSlimIdResponse> {
    return this.situViewClient.getHomeConfigSlimId(request);
  }
  
  @Cache({
    duration: 1800000
  })
  GetHomeConfig(request: GetHomeConfigRequest): Promise<GetHomeConfigResponse> {
    return this.situViewClient.getHomeConfig(request);
  }

  RefreshData() {
    this.oldCacheKey = this.cacheKey;
    this.cacheKey = Guid.create();
  }
}

export interface IHome {
  home: string;
}